/**
 * The voice-sample dialog as a page mounts it (ADR-0062), the same from both entries: a subject
 * being enrolled, and one already enrolled. It owns the microphone, the review's player and the
 * upload (`useVoiceSampleFlow`) and draws the pure dialog around them, with the question asked
 * before a recorded sample is dropped.
 *
 * Two modes. `collect`: the take the operator keeps is handed back (`onCollected`) and nothing is
 * sent — the caller holds it and sends it with `sendVoiceSampleTake` once there is a subject, or
 * at once where there is one already (`sendsAtOnce`, which only changes what the review says of
 * it). `upload`: the take is stored as the subject's `voice_sample` capture
 * (`onStored` names it), and the dialog then shows what the page learns of its job
 * (`captureStateOf`): the voiceprint is being computed, stored, or there was too little speech.
 *
 * The sample is only in this tab until it is stored. The host says so (`onUnsavedChange`) for
 * the page's own leave guard — the router takes one blocker, so the host adds none — and
 * `LoseVoiceSampleDialog` has the words for that guard's question (`reason="leave"`).
 */
import { useState } from 'react'
import { LoseVoiceSampleDialog } from '@/components/humint/voice/LoseVoiceSampleDialog'
import { VoiceSampleDialog } from '@/components/humint/voice/VoiceSampleDialog'
import { useModalVariant } from '@/features/humint/hooks/useModalVariant'
import {
  useVoiceSampleFlow,
  type VoiceSampleFlowDeps,
  type VoiceSampleFlowMode,
} from '@/features/humint/voice/useVoiceSampleFlow'
import { VoiceSampleFlowPanel } from '@/features/humint/voice/VoiceSampleFlowPanel'

export interface VoiceSampleHostProps {
  open: boolean
  /**
   * What becomes of the sample the operator keeps: handed back (`collect`), or stored with the
   * subject (`upload`, which also names the subject for the header).
   */
  mode: VoiceSampleFlowMode
  /** The dialog closes: its X, *Done*, or a kept take handed back. Set `open` to false. */
  onClose: () => void
  /**
   * A sample only this tab holds exists (true) or no longer does (false): one being recorded, or
   * recorded and not stored. Count it in the page's leave guard.
   */
  onUnsavedChange?: (unsaved: boolean) => void
  /** What a story hands in instead of the browser's microphone and the server; none in the app. */
  deps?: VoiceSampleFlowDeps
}

/** One opening of the dialog: its microphone, its sample and its upload live and die with it. */
function VoiceSampleSession({ open, mode, onClose, onUnsavedChange, deps }: VoiceSampleHostProps) {
  const variant = useModalVariant()
  const flow = useVoiceSampleFlow({ open, mode, onClose, onUnsavedChange, deps })
  const context =
    mode.kind === 'upload'
      ? { mode: mode.kind, subject: mode.subject }
      : { mode: mode.kind, sendsAtOnce: mode.sendsAtOnce }
  return (
    <>
      <VoiceSampleDialog open={open} variant={variant} onDismiss={flow.onDismiss}>
        <VoiceSampleFlowPanel flow={flow} context={context} />
      </VoiceSampleDialog>
      <LoseVoiceSampleDialog
        open={open && flow.discard.asking !== null}
        reason="discard"
        onKeep={flow.discard.onKeep}
        onLose={flow.discard.onLose}
      />
    </>
  )
}

/**
 * The voice-sample dialog. Every opening starts clean — on the instructions, with a microphone
 * of its own — whatever the one before it ended on.
 */
export function VoiceSampleHost(props: VoiceSampleHostProps) {
  const [opening, setOpening] = useState({ count: 0, open: props.open })
  if (props.open !== opening.open) {
    setOpening({ count: opening.count + (props.open ? 1 : 0), open: props.open })
  }
  return <VoiceSampleSession key={opening.count} {...props} />
}
